import { useState, useEffect } from 'react'

import { useApp } from '../../../app/AppContext.jsx'

import { fetchLocalJSON } from '../../../shared/utils/fetchLocalJSON.js'
import { FETCH_STATUS } from '../../../shared/constants/fetchStatus.js'

import { normalizeProduct } from '../utils/normalizeProduct.js'

/**
 * Fetches and normalizes the product list and building data for the current project.
 *
 * Product data source priority:
 *   1. WP plugin endpoint — returns CRM-enriched, panel-processed data structured
 *      as `{ [categoryKey]: product[] }` where keys come from `application_main_products`.
 *   2. Local `apartments/apartments.json` — fallback when the endpoint is
 *      unreachable (e.g. development without WP) or returns an error. Provides
 *      raw CAD data without CRM enrichment (status defaults to UNKNOWN).
 *
 * Products are extracted from ALL keys listed in
 * `projectConfig.crm_integration.product_categories_mapping.application_main_products`
 * (e.g. `['apartments', 'commercial_units']`). Falls back to `['apartments']` when
 * the config is not yet available.
 *
 * `buildings/buildings.json` is loaded from the local CAD package and is
 * required — failure to load it is a fatal error.
 *
 * Each product is passed through `normalizeProduct` regardless of source to
 * derive missing fields from `id_cad` and apply runtime defaults.
 *
 * @returns {{
 *   products: object[],
 *   interiorsData: Record<string, unknown[]>,
 *   rawBuildings: object[],
 *   rawCmsData: Record<string, unknown> | null,
 *   productsStatus: 'loading' | 'ready' | 'error',
 *   productsError: Error | null,
 *   interiorsError: Error | null,
 * }}
 */
export function useProducts() {
  const { windowData, projectConfig } = useApp()

  const [state, setState] = useState({
    products: [],
    interiorsData: {},
    rawBuildings: [],
    rawCmsData: null,
    productsStatus: FETCH_STATUS.LOADING,
    productsError: null,
    interiorsError: null,
  })

  useEffect(() => {
    if (!windowData?.projectDataUrl || !windowData?.siteUrl || !windowData?.projectId) return

    const { projectId, projectDataUrl, siteUrl } = windowData
    const mainProductKeys =
      projectConfig?.crm_integration?.product_categories_mapping?.application_main_products ?? ['apartments']
    let cancelled = false

    async function load() {
      // ── Step 1: products (endpoint → fallback) ────────────────────────────
      let productsData = null
      const timestamp = new Date().getTime(); //Cache-busting for proxy servers

      try {
        const response = await fetch(
          `${siteUrl}/wp-json/bim-cadaplus/v1/projects/${projectId}/products?_cb=${timestamp}`
        )
        if (response.ok) {
          productsData = await response.json()
        }
      } catch {
        // endpoint unreachable — fall through to local fallback
      }

      if (!productsData) {
        productsData = await fetchLocalJSON(
          `${projectDataUrl}apartments/apartments.json`,
          'useProducts: fallback apartments.json'
        )
      }

      if (cancelled) return

      if (!productsData) {
        setState({
          products: [],
          interiorsData: {},
          rawBuildings: [],
          rawCmsData: null,
          productsStatus: FETCH_STATUS.ERROR,
          productsError: new Error('Failed to load product data from endpoint and fallback file'),
          interiorsError: null,
        })
        return
      }

      // ── Step 2: buildings (required) ──────────────────────────────────────
      const buildingsData = await fetchLocalJSON(
        `${projectDataUrl}buildings/buildings.json`,
        'useProducts: buildings'
      )

      if (cancelled) return

      if (!buildingsData) {
        setState({
          products: [],
          interiorsData: {},
          rawBuildings: [],
          rawCmsData: null,
          productsStatus: FETCH_STATUS.ERROR,
          productsError: new Error('Failed to load buildings.json'),
          interiorsError: null,
        })
        return
      }

      // ── Step 3: interiors (optional) ─────────────────────────────────────
      let interiorsData = {}
      let interiorsError = null
      try {
        const loadedInteriors = await fetchLocalJSON(
          `${projectDataUrl}interiors/interiors.json`,
          'useProducts: interiors'
        )
        if (loadedInteriors && typeof loadedInteriors === 'object') {
          interiorsData = loadedInteriors
        } else if (loadedInteriors != null) {
          interiorsError = new Error('interiors.json has invalid shape (expected object)')
        }
      } catch (err) {
        interiorsError = err instanceof Error ? err : new Error(String(err))
      }

      // ── Step 4: normalize and publish ────────────────────────────────────
      // Extract products from all application_main_products keys and merge into one list.
      const rawProducts = mainProductKeys.flatMap(key =>
        Array.isArray(productsData[key]) ? productsData[key] : []
      )
      const buildings = buildingsData.buildings ?? []

      // Build a lookup map for human-readable building names (from buildings.json).
      // Used to enrich each product with building_name for display purposes.
      const buildingNameMap = Object.fromEntries(
        buildings.map(b => [b.id_cad, b.name_original]).filter(([k]) => k)
      )

      const products = rawProducts.map(raw => {
        const normalized = normalizeProduct(raw)
        return {
          ...normalized,
          building_name: normalized.building_name ?? buildingNameMap[normalized.building_id] ?? normalized.building_id ?? null,
        }
      })

      const productsSorted = [...products].sort((a, b) => (a.id_cad ?? '').localeCompare(b.id_cad ?? ''))
      // const buildingsSorted = [...buildings].sort((a, b) => (a.id_cad ?? '').localeCompare(b.id_cad ?? ''))
      const buildingsSorted = [...buildings]
        .map(building => ({
          ...building,
          tiers: building.tiers 
            ? [...building.tiers].sort((a, b) => (a.ref_tier ?? 0) - (b.ref_tier ?? 0))
            : []
        }))
        .sort((a, b) => (a.id_cad ?? '').localeCompare(b.id_cad ?? ''));

      setState({
        products: productsSorted,
        interiorsData,
        rawBuildings: buildingsSorted,
        rawCmsData: productsData,
        productsStatus: FETCH_STATUS.READY,
        productsError: null,
        interiorsError,
      })
    }

    load().catch((err) => {
      if (cancelled) return
      setState({
        products: [],
        interiorsData: {},
        rawBuildings: [],
        rawCmsData: null,
        productsStatus: FETCH_STATUS.ERROR,
        productsError: err instanceof Error ? err : new Error(String(err)),
        interiorsError: null,
      })
    })

    return () => {
      cancelled = true
    }
  }, [windowData, projectConfig])

  return state
}
